import { Metadata } from 'next';
import { OpenGraph } from 'next/dist/lib/metadata/types/opengraph-types';

import {
  ArticleJsonLdProps,
  BlogPostDocument,
  BreadCrumbJsonLdProps,
  CarouselJsonLdProps,
  CustomerReview,
  GeneralCardSlice,
  IBreadcrumbItem,
  ItemListElements,
  LocalBusinessJsonLdProps,
  Product,
  ProductJsonLdProps,
  SeoContentSlice,
  TreezStore,
  TSeoProps,
  TwitterCardSlice,
} from '@/types';
import { TEMPLATE_BASE_PATH } from '@/constants';
import {
  createLocationUrl,
  currencyFormat,
  getTreezSeoAddress,
  getTreezSeoOpeningHours,
  sanitizeTreezSearchParams,
} from '@/utils';
import { Twitter } from 'next/dist/lib/metadata/types/twitter-types';

const seoContentBuilder = ({
  seoData: { generalCard, seoContent, twitterCard },
  canonicalUrl: baseCanonicalUrl,
  fallbackTitle,
  page,
}: {
  seoData: ISeo;
  canonicalUrl?: string;
  /** Used when Prismic SEO title is missing/blank so the document always has a title. */
  fallbackTitle?: string;
  /** Listing page from `getListingCanonicalPage`: adds `?page=N` to the canonical and "Page N" to the title. */
  page?: number;
}): Metadata => {
  const prismicTitle = seoContent?.primary?.title?.trim();
  const fallback = fallbackTitle?.trim();
  const baseTitle = prismicTitle || fallback || undefined;
  const title = baseTitle && page ? `${baseTitle} - Page ${page}` : baseTitle;
  const canonicalUrl =
    baseCanonicalUrl && page
      ? `${baseCanonicalUrl}?page=${page}`
      : baseCanonicalUrl;

  return {
    description: seoContent?.primary?.description,
    openGraph: getOpenGraph(generalCard, canonicalUrl),
    // Omit empty title so the Storefront root `title.default` (store name) is kept.
    ...(title ? { title } : {}),
    keywords: seoContent?.primary?.keywords,
    twitter: getTwitterCard(twitterCard, generalCard?.primary?.site_name ?? ''),
    robots: {
      index: !seoContent?.primary?.noindex,
      follow: !seoContent?.primary?.nofollow,
    },
    ...(canonicalUrl ? { alternates: { canonical: canonicalUrl } } : {}),
  };
};

const getListingCanonicalPage = (
  searchParams: Record<string, string | string[]> = {},
): number | undefined => {
  const { page, ...listingParams } = sanitizeTreezSearchParams({
    searchParams,
  });
  const pageNumber = parseInt(String(page ?? ''));

  if (Object.keys(listingParams).length > 0 || !(pageNumber >= 2)) {
    return undefined;
  }

  return pageNumber;
};

const withSeoCanonical = ({
  metadata,
  canonicalUrl,
}: {
  metadata: Metadata;
  canonicalUrl: string;
}): Metadata => ({
  ...metadata,
  alternates: {
    ...metadata.alternates,
    canonical: canonicalUrl,
  },
  ...(metadata.openGraph
    ? {
        openGraph: {
          ...metadata.openGraph,
          url: canonicalUrl,
        },
      }
    : {}),
});

type TSeoEl = TwitterCardSlice | SeoContentSlice | GeneralCardSlice;

const getSeoProps = (body: TSeoProps): ISeo => {
  const seo: ISeo = {};

  if (!body || typeof body !== 'object') {
    return seo;
  }

  Object?.values(body)?.forEach((el: TSeoEl) => {
    switch (el?.slice_type) {
      case 'seo_content':
        seo.seoContent = el;
        break;

      case 'general_card':
        seo.generalCard = el;
        break;

      case 'twitter_card':
        seo.twitterCard = el;
        break;

      default:
        break;
    }
  });

  return seo;
};

const checkEnvNoIndexNoFollow = () => {
  // this code is really dangerous it is for not tracking boot indexing
  // on the dev environments
  // https://developers.google.com/search/docs/crawling-indexing/block-indexing
  if (process.env.ENV && process.env.ENV === 'dev') {
    return { nofollow: true, noindex: true };
  }
  return null;
};

const getTwitterCard = (
  twitterCard?: TwitterCardSlice,
  siteName?: string,
): Twitter | undefined => {
  if (!twitterCard) {
    return undefined;
  }

  return {
    // TODO: add this types to prismic slice
    card: 'summary_large_image',
    images: [
      {
        alt: twitterCard.primary.image?.alt ?? '',
        height: twitterCard.primary.image?.dimensions?.height ?? 630,
        type: getImageTypeFromUrl(twitterCard.primary.image?.url),
        url: twitterCard.primary.image?.url ?? '',
        width: twitterCard.primary.image?.dimensions?.width ?? 1200,
      },
    ],
    site: siteName,
  };
};

const getOpenGraph = (
  generalCard?: GeneralCardSlice,
  canonicalUrl?: string,
): OpenGraph | undefined => {
  if (!generalCard) {
    return undefined;
  }

  return {
    description: generalCard.primary?.description ?? '',
    images: [
      {
        alt: generalCard.primary?.image?.alt ?? '',
        height: generalCard.primary?.image?.dimensions?.height ?? 1250,
        url: generalCard.primary?.image?.url ?? '',
        width: generalCard.primary?.image?.dimensions?.width ?? 870,
      },
    ],
    siteName: generalCard.primary?.site_name ?? '',
    title: generalCard.primary?.title ?? '',
    type: 'website',
    ...(canonicalUrl ? { url: canonicalUrl } : {}),
  };
};

const getProductOffer = (product: Product, storeName: string) => {
  return {
    availability: `http://schema.org/${
      product.in_stock ? 'InStock' : 'OutOfStock'
    }`,
    itemCondition: 'NewCondition',
    price: `${currencyFormat((product?.variants ?? [])[0]?.price ?? 100)}`,
    priceCurrency: 'USD',
    seller: {
      name: storeName,
    },
    url: `${process.env.NEXT_PUBLIC_STORE_DOMAIN}${product?.url}`,
  };
};

const seoCarouselJsonLdBuilder = (
  products: Product[],
  storeName: string,
): CarouselJsonLdProps => {
  const data = products?.map((p: Product) => {
    const ratingValue = p?.review_rating;
    const reviewCount = p?.reviews;
    const showAggregateRating = !!ratingValue && reviewCount! > 0;

    const prop: ProductJsonLdProps = {
      ...(showAggregateRating
        ? {
            aggregateRating: {
              ratingValue: `${ratingValue}`,
              reviewCount: `${reviewCount}`,
            },
          }
        : {}),
      brand: p?.brand_name ?? '',
      description: p?.description ?? '',
      name: p?.name ?? '',
      // sku: p?.sku,
      offers: getProductOffer(p, storeName),
    };

    if (p?.asset?.image) {
      prop.image = p?.asset?.image;
    }

    return prop;
  });

  return { data, ofType: 'product' };
};

const seoBreadCrumbJsonLdBuilder = (
  items: IBreadcrumbItem[],
): BreadCrumbJsonLdProps => {
  return {
    itemListElements: items.map(
      (i: IBreadcrumbItem, index: number): ItemListElements => {
        return {
          ...(i.href ? { item: i.href } : {}), // TODO add base domain
          name: i.label,
          position: index + 1,
        };
      },
    ),
  };
};

const seoArticleJsonLdBuilder = (
  page: BlogPostDocument<string>,
): ArticleJsonLdProps => {
  const domain: string = process?.env?.NEXT_PUBLIC_STORE_DOMAIN ?? '';
  const blog: string = TEMPLATE_BASE_PATH?.BLOG;

  return {
    authorName: '',
    dateModified: page.last_publication_date,
    datePublished: page.first_publication_date,
    description: page?.data?._seo_content_?.[0]?.primary?.description ?? '',
    images: [],
    title: page?.data?._seo_content_?.[0]?.primary?.title ?? '',
    url: `${domain}/${blog}${page.uid}`,
  };
};

const seoProductJsonLdBuilder = (
  product: Product,
  reviews: CustomerReview[],
  storeName: string,
): ProductJsonLdProps => {
  const ratingValue = product?.review_rating;
  const reviewCount = reviews.length;
  const showAggregateRating = !!ratingValue && reviewCount > 0;
  const hasReviews = reviewCount > 0;

  return {
    ...(showAggregateRating
      ? {
          aggregateRating: {
            ratingValue: `${ratingValue}`,
            reviewCount: `${reviewCount}`,
          },
        }
      : {}),
    brand: product?.brand_name ?? '',
    description: product?.description ?? '',
    image: product.asset?.image ?? '',
    name: product.name ?? '',
    offers: getProductOffer(product, storeName),
    ...(hasReviews
      ? {
          reviews: reviews?.map(review => ({
            author: review?.nickname,
            datePublished: review?.created_at,
            reviewBody: review?.comment,
            reviewRating: { ratingValue: `${review?.rating}` },
          })),
        }
      : {}),
    // sku: product?.sku,
    type: 'Product',
  };
};

const domain = process?.env?.NEXT_PUBLIC_STORE_DOMAIN ?? '';
const reverseProxyLink = process.env.NEXT_PUBLIC_REVERSE_PROXY_LINK ?? '';

const domainWithReverseProxy = `${domain}${reverseProxyLink}`;

const seoLocalBusinessJsonLdBuilder = (
  store: TreezStore,
  parentOrganization?: string | null,
): LocalBusinessJsonLdProps => {
  const ratingValue = store.rating < 1 ? 1 : store.rating;
  const reviewCount = store.reviews_count;
  return {
    address: getTreezSeoAddress(store),
    description: store.description,
    geo: {
      latitude: `${store.geoloc.lat}`,
      longitude: `${store.geoloc.lng}`,
    },
    id: `${domainWithReverseProxy}/dispensary/${store.url_slug}/${store.object_id}`,
    ...(parentOrganization
      ? {
          parentOrganization: {
            '@id': `${domainWithReverseProxy}/${parentOrganization}`,
          },
        }
      : {}),
    name: store.name,
    openingHours: getTreezSeoOpeningHours(store),
    rating: {
      ratingValue: `${ratingValue}`,
      ...(reviewCount > 0 ? { reviewCount: `${reviewCount}` } : {}),
    },
    telephone: store.phone,
    type: 'Store',
    url: createLocationUrl(store.url_slug),
  };
};

const getImageTypeFromUrl = (url?: string | null) => {
  if (!url) {
    return undefined;
  }

  const regex = /(?<=\.)[^.?]*(?=\?)/;
  const match = url.match(regex);
  return `image/${match?.[0]}`;
};

export interface ISeo {
  generalCard?: GeneralCardSlice;
  twitterCard?: TwitterCardSlice;
  seoContent?: SeoContentSlice;
}

export {
  seoArticleJsonLdBuilder,
  seoContentBuilder,
  seoCarouselJsonLdBuilder,
  seoBreadCrumbJsonLdBuilder,
  seoProductJsonLdBuilder,
  seoLocalBusinessJsonLdBuilder,
  checkEnvNoIndexNoFollow,
  getSeoProps,
  getListingCanonicalPage,
  withSeoCanonical,
};
